export const TRAINING_TEST_STEPS = [
  "basics",
  "materials",
  "questions",
  "audience",
  "review",
] as const;

export type TrainingTestStep = (typeof TRAINING_TEST_STEPS)[number];

export function parseTrainingTestStep(
  value: string | null
): TrainingTestStep {
  if (value && TRAINING_TEST_STEPS.includes(value as TrainingTestStep)) {
    return value as TrainingTestStep;
  }
  return "basics";
}

export function stepIndex(step: TrainingTestStep): number {
  return TRAINING_TEST_STEPS.indexOf(step);
}

export function isQuestionValid(q: {
  questionText?: string;
  correctOptionIds?: string[];
  options?: { text: string }[];
}): boolean {
  return Boolean(
    q.questionText?.trim() &&
      (q.correctOptionIds || []).length > 0 &&
      (q.options || []).every((o) => o.text.trim())
  );
}

export function countValidQuestions(
  questions: Parameters<typeof isQuestionValid>[0][]
): number {
  return questions.filter(isQuestionValid).length;
}

export interface TrainingStepCompletionInput {
  test: {
    name?: string;
    status?: string;
    materials?: unknown[];
    questions?: unknown[];
    questionsCount?: number;
  } | null;
  questions: Parameters<typeof isQuestionValid>[0][];
  selectedRoleIds: string[];
  assignmentsCount: number;
}

export function getTrainingStepCompletion(
  input: TrainingStepCompletionInput
): Record<TrainingTestStep, boolean> {
  const { test, questions, selectedRoleIds, assignmentsCount } = input;
  const savedQuestionCount =
    typeof test?.questionsCount === "number"
      ? test.questionsCount
      : (test?.questions || []).length;
  const validSavedQuestions = countValidQuestions(
    (test?.questions as Parameters<typeof isQuestionValid>[0][]) || questions
  );

  return {
    basics: Boolean(test?.name?.trim()),
    materials: (test?.materials || []).length > 0,
    questions: savedQuestionCount > 0 && validSavedQuestions > 0,
    audience: selectedRoleIds.length > 0 || assignmentsCount > 0,
    review: test?.status === "active",
  };
}

export function canActivateTrainingTest(
  completion: Record<TrainingTestStep, boolean>
): boolean {
  return (
    completion.basics && completion.questions && completion.audience
  );
}
